/**
 * @file Helper functions for GraphQL cache resolvers
 * @see https://formidable.com/open-source/urql/docs/graphcache/local-resolvers/
 */

import {
  Cache,
  DataField,
  FieldInfo,
  Variables,
} from '@urql/exchange-graphcache';
import { stringifyVariables } from 'urql';

/**
 * Get all cache fields that have the same name as the
 * query that is in question (aka messageSearch)
 *
 * @param allFields All fields in the cache
 * @param queryName The name of the query
 * @returns         All fields that have the same name as the query
 */
export const getAllCachedFieldsForQueryName = (
  allFields: FieldInfo[],
  queryName: string,
) => {
  const fieldInfos = allFields.filter(filedInfo => {
    return filedInfo.fieldName === queryName;
  });

  return fieldInfos;
};

/**
 * Get the cache key for a field
 *
 * @param fieldArgs The arguments passed to the most recent query
 * @param fieldName The name of the field
 * @returns         The cache key for the field
 */
export const getCacheKeyForField = (
  fieldArgs: Variables,
  fieldName: string,
): string => {
  return `${fieldName}(${stringifyVariables(fieldArgs)})`;
};

/**
 * Ensures that the cache key exists and
 * gives proper Typescript support
 *
 * @param value Resolved cache value
 * @returns     Resolved key or null if not resolved
 */
export const ensureKey = (value: DataField): string | null => {
  return typeof value === 'string' ? value : null;
};

/**
 * Get the cache value for a field
 *
 * @param cache     urql cache instance
 * @param entity    A GraphQL object (“entity”) or an entity key.
 * @param fieldName The name of the field
 * @returns         The resolved cache value or null if not resolved
 */
export const getRetrievedCacheFieldAsString = (
  cache: Cache,
  entity: string,
  fieldName: string,
) => {
  const dataField = cache.resolve(entity, fieldName);

  if (!dataField) {
    return null;
  }

  if (typeof dataField !== 'string') {
    return null;
  }

  return dataField;
};
